/**
 * CWOS canonical copy (doc 03 §3.7 + doc 07 + doc 09 §8).
 *
 * All user-facing strings are keyed here and used VERBATIM in-app (never
 * paraphrased). Params use `{name}` ICU-style placeholders resolved by `t()`.
 * Fixed-copy components (WhyNote, StalenessNote, PolicyCard, AttestationFlow,
 * QueueList footer, PrivacyFootnote, TrailGateButton, ReopenBanner) expose no
 * string-override props — they resolve these keys directly.
 */
const en = {
    'filters.none': "That's everything — no hidden filters.",
    'original.kept': 'kept — nothing was erased',
    'timeclock.unaffected': "Your timeclock isn't affected.",
    'verify.neverAgainst': 'never counted against you',
    'verify.neverVerified': 'never shown as verified',
    'severity.look': 'Worth a look',
    'severity.heads': 'Just a heads-up',
    'autoclose.real': 'closes on its own, keeping your real times',
    'approve.lock.one': 'Approve — lock {n}',
    'approve.lock.many': 'Approving locks {n}.',
    audited: 'recorded',
    'gap.fixed': 'Basements, weather, battery saver — phones do this. A heads-up, not a finding.',
    'sync.intact': 'paused before changing anything — nothing was changed or deleted',
    'crumbs.sessionOnly': 'collected while clocked in only',
    'crumbs.neverAfter': 'during your shift — never after',
    // Verification badges (doc 03 §3.5 — fixed, both apps).
    'verify.atWorkSite': 'At a work site',
    'verify.atGeofence': 'At {geofence}',
    'verify.awayFromSites': 'Away from work sites',
    'verify.locationNoted': 'Location noted',
    'verify.couldntConfirm': "Couldn't confirm spot",
    'verify.noReading': 'No location reading',
    'verify.locationOff': 'Location off on phone',
    'verify.notCollected': 'Not collected (policy)',
    'verify.attested': 'Confirmed by {first name}',
    // Greeting (doc 04 §5).
    'greeting.morning': 'Good morning',
    'greeting.afternoon': 'Good afternoon',
    'greeting.evening': 'Good evening',
    'greeting.nothingNeedsYou': 'Nothing needs you right now.',
    // The four states that must never render as one another
    // (docs/withholding-contract.md). A permission-denied, a withheld value, a
    // failed query and a true zero are four different sentences, and the defect
    // this product ranks worst is printing one of them for another. These are
    // keyed here, rather than written at each of the sixty call sites, for the
    // same reason WITHHELD_LABEL is one phrase: a person who meets "Not shown to
    // you" on a program lead and "Restricted" on a compliance tile has to learn
    // twice that they mean the same thing.
    'state.notShown': 'Not shown to you',
    'state.didntLoad': 'Didn’t load',
    'figure.refused': 'Your account may not see this figure. It is not zero — it is withheld.',
    'figure.failed': 'This figure did not load. It is not zero.',
    // Said about a count computed over a subset the reader's grants pick out. It
    // names the population rather than guessing how many rows the scope removed —
    // the client cannot know that, and a guess is the same lie pointed the other
    // way.
    'figure.counts': 'Counts {who}, not the whole organization.',
    'figure.scopeUnknown': 'We could not check how far your access reaches, so this may count fewer people than the organization has.',
    // A rate carries its denominator's population into the words, because a
    // percentage over a narrowed population looks exactly like the organization's
    // — it is the same two digits.
    'rate.of': '{percent}% of {of}',
    'rate.none': 'No {what} to measure in {of}.',
    'rate.population.org': 'the organization',
    'rate.population.unknown': 'the people you can see',
    // A dropdown that cannot be operated must say why. Four states, four
    // sentences — the empty one is the only branch entitled to a claim about the
    // organization.
    'picker.loading': 'Loading {what}…',
    'picker.refusedOption': 'Not shown to you',
    'picker.refused': 'You may not see the {what} in this organization, so there is nothing here to choose from. The list is withheld, not empty.',
    'picker.failedOption': 'Couldn’t load {what}',
    'picker.failed': 'The {what} did not load. This list is empty because of that, not because your organization has none.',
    'picker.empty': 'No {what} yet',
    // The counterpart to `filters.none` for a reader whose own grants narrow the
    // view. `filters.none` promises completeness; saying it to a scoped reader is
    // an affirmative false statement made by the one component that is mandatory
    // on every list screen.
    'filters.scoped': 'Not everything — your access narrows this to {who}.',
    // Empty states (doc 07 §7.2).
    'empty.successAttention': 'Nothing urgent is being missed — and when this says so, you can trust it. We never hide anything to keep things looking tidy.',
    'empty.filtered': 'Nothing matches these filters.',
    'empty.filtered.clear': 'Clear filters',
    'empty.notYetObserved': "We've been watching since {from}. History before that isn't shown because we can't vouch for it.",
    'empty.trailSoon': 'Trail available soon',
    'empty.toWrite': 'Nothing left to write. Enjoy it.',
    'empty.noShift': 'Nothing scheduled today.',
    // The scheduling board is the ORGANIZATION'S CALENDAR: worker shifts AND
    // client visits (planned in Phibian or carried in from an EHR). Its empty
    // state therefore speaks for both — and, for a reader who cannot read
    // visits, says the zero is over shifts alone rather than letting it read as
    // "nothing is happening".
    'schedule.emptyWindow': 'No shifts and no client visits fall in this window.',
    'schedule.emptyShiftsOnly': 'No shifts fall in this window. Client visits are not shown to you here, so this says nothing about them.',
    'schedule.visitsWithheld': 'Client visits are not on this calendar for you — reading them needs the service permission. Only shifts are shown.',
    'schedule.visitsFailed': 'We couldn’t load client visits just now, so only shifts are shown. Do not read missing visits as none.',
    'schedule.siteFilterShiftsOnly': 'Client visits carry no site, so a site filter shows shifts only.',
    'schedule.shiftListOnly': 'This list holds worker shifts only; client visits are on the Week, Day and Month calendar.',
    'schedule.visitReadOnly': 'Read-only here. The EHR owns this visit; Visits is where Phibian records against it.',
    // Provenance (PH-UX-002; AGENTS.md §5 — "never display data from a degraded
    // integration without saying so"). `services.ts`'s own contract comments are
    // the source of truth these mirror; `now-next.ts`'s two worker sentences
    // ("Booked in the EHR." / "The EHR is no longer listing it") are the
    // reference tone, adapted from a worker deciding whether to go to a
    // supervisor reading someone else's record after the fact.
    'provenance.bookedIn': 'Booked in {name}.',
    'provenance.bookedInEhr': 'Booked in the EHR.',
    'provenance.vendorAbsentPill': 'No longer listed by the EHR',
    // AN OBSERVATION, NOT A CANCELLATION (services.ts's own words) — this must
    // never be read as "called off", and never overwrite the record's own
    // status pill with one.
    'provenance.vendorAbsent': 'The EHR is no longer listing this — an observation, not a cancellation. The decision is yours.',
    'provenance.lastSynced': 'Last synced {t}.',
    'provenance.connectorDegraded': '{name} needs attention in the Integration Center — records it synced may be behind.',
    'provenance.openIntegrationCenter': 'Open the Integration Center',
    // Accounts.
    'account.suspended': 'Your account is suspended. Contact your org admin to continue.',
    'account.deactivated': 'Your account was deactivated. Contact your org admin.',
    // WhyNote variants (doc 04 §18 — fixed copy).
    'why.lowAccuracy': 'too fuzzy (about {dist}) to say where — so we say *couldn\u2019t confirm* rather than guessing. It never counts against {name}.',
    'why.attested': 'You attested to where this happened. A supervisor may review it.',
    'why.offlinePunch': 'saved on the phone offline, uploaded {t}. The real time is what counts, always.',
    // Trust footer (doc 02 W5.1 — fixed).
    'trust.timesYours': 'Your times are yours: what your phone records is what counts, even offline. Fixes sit next to originals — nothing is ever overwritten. You can always see exactly what\u2019s been collected about you.',
    // Privacy (doc 05 §14 + §15 — fixed).
    'privacy.footnote': 'Clients appear by initials to protect their privacy. Full names open only when needed — and that\u2019s recorded.',
    'privacy.revealConfirm': 'Viewing the full name is recorded',
    // Attestation (doc 05 §17 — 8th-grade level, fixed).
    'attest.beat1': "Your phone couldn't get a GPS fix in time. This often happens inside buildings or in bad weather — it doesn't block you from recording your time.",
    'attest.beat2': "Your punch is saved as **\u2018Worker attested\u2019** — it's never shown as verified, and your supervisor may review it.",
    'attest.beat3.label': 'Add a note (optional)',
    'attest.confirm': 'Attest & clock in',
    'attest.cancel': "Cancel — don't clock in",
    // Offline (doc 05 §20, §21, §23 — fixed).
    'offline.banner': "You're offline — actions are saved and will sync when you reconnect.",
    'offline.punchReceipt': "It will upload when you're back online — your punch time never changes to the upload time.",
    'queue.retriesSafe': 'Retries are safe — each event carries one ID, so a resend can never create a duplicate punch.',
    'queue.encryptedOnDevice': 'encrypted on device',
    // DeliveryBand (doc 04 §6).
    'delivery.question': "Are today's services actually happening?",
    // The two directions of time reconciliation. They are different problems with
    // different owners, and the band used to show one under the other's label.
    'delivery.visitsWithoutTime': 'visits without a matching time record',
    'delivery.timeWithoutVisit': 'time records without a matching visit',
    'delivery.asOf': 'as of {t}',
    // EVV: confirmed is not compliant (ADR 0017 §8, OWNER-BLOCKERS §2d).
    //
    // Two axes, two badges, and the words are chosen so that neither one can be
    // read as the other. "Confirmed" says who attested; it makes no claim about
    // whether the visit will be accepted. The compliance words name a verdict and
    // say whose verdict it is, because the rule belongs to an adapter and this
    // product must never imply it invented one.
    //
    // "Not compliant" is deliberately not an alarm and not a failure: ADR 0017 §8
    // requires the record be submitted anyway. `evv.mustStillSubmit` is the
    // sentence that stops a reader treating it as something to delete or paper
    // over.
    'evv.confirmed': 'Confirmed',
    'evv.notConfirmed': 'Not confirmed',
    'evv.compliant': 'Compliant',
    'evv.noncompliant': 'Not compliant',
    'evv.complianceUnchecked': 'Not checked',
    'evv.confirmedIsNotCompliant': 'Confirmed means someone attested this visit happened. Whether it carries the evidence a payer requires is a separate question, answered on the right.',
    'evv.mustStillSubmit': 'A visit that is not compliant is still submitted. It is a record, not a mistake to hide.',
    'evv.complianceUncheckedWhy': 'No compliance rule is installed for this program, so nothing has checked this visit either way.',
    'evv.correctionCosts': 'Correcting a visit keeps the original and files a replacement. Both stay in the count, and a corrected visit does not count as compliant.',
    // Consequence copy (doc 01 A4.2 etc.).
    'consequence.approveLocks': 'Approving locks {total}. If anything changes later, the week comes back to you automatically.',
    'consequence.timeoffQuiets': 'Approving also quiets missing-hours flags for these days, on purpose.',
    'consequence.reopenApproved': 'fixing this reopens {name}\u2019s approved week for a fresh look — automatically.',
};
/**
 * Resolve a keyed string with ICU-style `{param}` substitution. Missing params
 * are left literal (never silently dropped to a wrong sentence) — callers pass
 * the full param set.
 */
export function t(key, params) {
    let s = en[key];
    if (params) {
        for (const [k, v] of Object.entries(params)) {
            s = s.split(`{${k}}`).join(String(v));
        }
    }
    return s;
}
/** All canonical strings, for reading-level / banned-emoji checks. */
export const copyCatalog = en;
//# sourceMappingURL=en.js.map